import * as Sentry from "@sentry/react";

import { useTexts } from "Components/TextContext";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { Form, FormikProps } from "formik";

import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { StyledComparisonWrap } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/productPriceTile.styles";
import { BackendErrorAlert } from "Components/Form/CommonFormElements/BackendErrorAlert/BackendErrorAlert";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { HuseierneHouseContentsCoverages } from "Features/CheckPrice/Products/RiskProducts/HouseProducts/HuseierneHouseContents/ProductPrice/HuseierneHouseContentsCoverages";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { HuseierneHouseContentsCoverage } from "Store/interfaces/products/coverage";
import { ReduxState } from "Store/rootReducer";
import React from "react";
import { useSelector } from "react-redux";
import { HUSEIERNE_HOUSE_CONTENTS_PRICE_STEP } from "..";
import { HuseierneHouseContentsProductPriceFormValues } from "../huseierneHouseContentsFormValuesInterfaces";
import { validationSchema } from "./huseierneHouseContentsProductPriceValidation";

export const HuseierneHouseContentsProductPrice = (): React.ReactElement => {
  const { getText } = useTexts();
  const handleAddProductToCart = useAddProductToCartHandler(
    HUSEIERNE_HOUSE_CONTENTS_PRICE_STEP.id,
  );
  const { priceCalculation, addProductToCart } = useSelector(
    (state: ReduxState) => state,
  );

  const initialFormState =
    useFormState<HuseierneHouseContentsProductPriceFormValues>(
      HUSEIERNE_HOUSE_CONTENTS_PRICE_STEP.id,
      {
        coverage: HuseierneHouseContentsCoverage.HUSEIERNE_HOUSE_CONTENTS_SUPER,
      },
    );

  const handleSubmit = (
    formProps: HuseierneHouseContentsProductPriceFormValues,
  ): void => {
    const productToAdd = {
      coverage: formProps.coverage,
      productType: INSURANCE_NAME_VALUE.HUSEIERNE_HOUSE_CONTENTS,
    };
    handleAddProductToCart(formProps, [productToAdd]);
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductPrice - ${INSURANCE_NAME_VALUE.HUSEIERNE_HOUSE_CONTENTS}`,
        );
      }}
    >
      <ProductPriceTile
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        header={getText("pageheader.huseiernehousecontents.checkprice")}
      >
        {(
          formProps: FormikProps<HuseierneHouseContentsProductPriceFormValues>,
        ) => {
          return (
            <Form>
              <HuseierneHouseContentsCoverages formProps={formProps} />
              <BackendErrorAlert
                priceCalculation={priceCalculation.priceCalculationResult}
                addProductToCart={addProductToCart}
              />
              <StyledComparisonWrap>
                <NavigationButtons isLoading={addProductToCart.isLoading} />
              </StyledComparisonWrap>
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.HUSEIERNE_HOUSE_CONTENTS}
                stepId={HUSEIERNE_HOUSE_CONTENTS_PRICE_STEP.id}
              />
            </Form>
          );
        }}
      </ProductPriceTile>
    </Sentry.ErrorBoundary>
  );
};
